import React, { useState, useEffect, useCallback } from "react";
import { PageLayout } from "../../components/page-layout";
import { OnboardingProgressBar } from "../../components/onboarding-progress-bar";
import { useOnboarding } from "../../providers/onboarding-provider";
import { useAuth0 } from "@auth0/auth0-react";
import { getAwaitCountersignStatus } from "../../services/backend.service";
import { PageLoader } from "../../components/page-loader";
import { FormShell } from "../../components/onyx/FormShell";
const POLL_INTERVAL_MS = 60000;

export const AwaitCountersignPage = () => {
  const { fetchOnboardingStatus, navigateToLatestStep, currentStep } = useOnboarding();
  const { getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(true);
  const [statusData, setStatusData] = useState(null);
  const [error, setError] = useState(null);

  const advanceIfPastAwait = useCallback(async (awaitStatus) => {
    if (!awaitStatus || awaitStatus.current_step === "AWAIT_COUNTERSIGN") {
      return false;
    }
    // Prefer status from onboarding API (includes step_sequence paths),
    // but always navigate from the response — not stale React state.
    const refreshed = await fetchOnboardingStatus({ silent: true });
    navigateToLatestStep(refreshed || awaitStatus);
    return true;
  }, [fetchOnboardingStatus, navigateToLatestStep]);

  const pollStatus = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const { data, error: apiError } = await getAwaitCountersignStatus(token);
      if (apiError) {
        setError(apiError);
        return;
      }
      setStatusData(data);
      setError(null);
      await advanceIfPastAwait(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, advanceIfPastAwait]);

  useEffect(() => {
    // Provider may already know we've advanced (e.g. after webhook + refresh)
    if (currentStep && currentStep !== "AWAIT_COUNTERSIGN") {
      navigateToLatestStep({ current_step: currentStep });
      return;
    }
    pollStatus();
    const interval = setInterval(pollStatus, POLL_INTERVAL_MS);
    const onFocus = () => pollStatus();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [pollStatus, currentStep, navigateToLatestStep]);

  if (loading && !statusData) {
    return <PageLoader />;
  }

  return (
    <PageLayout>
      <div className="content-layout">
        <OnboardingProgressBar />
        <div className="content-layout__main onyx-page-narrow">
          <FormShell>
            <h1 className="content__title">You&apos;re all set for now</h1>
            <p className="content__body">
              Thank you for enrolling! Attune staff will review and countersign
              your consent form soon, and we&apos;ll email you a signed copy for
              your records.
            </p>
            <p className="content__body">
              That same email will include a link to schedule your study visit
              at Attune Health. You can safely close this page.
            </p>

            {error && (
              <p className="error-message">
                {error.message || "Unable to check status right now. We'll keep trying in the background."}
              </p>
            )}
          </FormShell>
        </div>
      </div>
    </PageLayout>
  );
};
